import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { ExpertCard, ExpertPage, ExpertProfile } from '@dunea/shared';
import { apiGetExpert, apiListExperts, apiRequestExpert } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Section,
  Select,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  completedLabel,
  countryName,
  expertFromLabel,
  expertRequestBody,
  expertRequestErrors,
  EXPERT_COUNTRIES,
  portfolioLinks,
  REQUEST_MESSAGE_MAX,
  serviceLine,
  type ExpertRequestDraft,
} from './community-model';
import { apiErrorMessage } from './marketplace-model';
import { ListingCard, ListingIcon, VerifiedBadge } from './parts';
import { useDebounced } from './use-listings';

/** The Experts directory: people who build Dunea sites for hire. */
export function ExpertsView({ onOpen }: { onOpen: (handle: string) => void }) {
  const [q, setQ] = useState('');
  const [service, setService] = useState('');
  const [country, setCountry] = useState('');
  const settledQ = useDebounced(q);
  const settledService = useDebounced(service);
  const key = `${settledQ}|${settledService}|${country}`;
  const first = useResource(
    () => apiListExperts({ q: settledQ, service: settledService, country: country || undefined }),
    key,
  );
  const [more, setMore] = useState<{ key: string; page: ExpertPage; items: ExpertCard[] } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const extra = more?.key === key ? more : null;
  const items = first.data ? [...first.data.items, ...(extra?.items ?? [])] : [];
  const hasMore = extra ? extra.page.hasMore : Boolean(first.data?.hasMore);

  const loadMore = async () => {
    const page = (extra?.page.page ?? 1) + 1;
    setLoadingMore(true);

    try {
      const next = await apiListExperts({ q: settledQ, service: settledService, country: country || undefined, page });
      setMore({ key, page: next, items: [...(extra?.items ?? []), ...next.items] });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load more experts.'));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[12px] leading-relaxed text-secondary">
        Hire someone to build or finish your site. They work in Dunea and hand the site to you.
      </p>
      <div className="space-y-2">
        <div className="relative">
          <div className="i-ph:magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search experts"
            aria-label="Search experts"
            className="ui-input !py-2 pl-8"
          />
        </div>
        <div className="flex gap-2">
          <input
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="Service, like shop setup"
            aria-label="Service"
            className="ui-input min-w-0 flex-1 !py-2"
          />
          <Select value={country} onChange={setCountry} label="Country" className="w-[150px] shrink-0">
            <option value="">Any country</option>
            {EXPERT_COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {first.error && !first.loading ? (
        <LoadError message="Could not load experts." onRetry={() => void first.reload()} />
      ) : !first.data ? (
        <div className="space-y-2.5">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : items.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center text-[12px] text-muted')}>
          {q || service || country ? 'No experts match. Try a wider search.' : 'No experts listed yet.'}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <ExpertRow key={e.handle} expert={e} onOpen={() => onOpen(e.handle)} />
          ))}
          {hasMore && (
            <div className="flex justify-center pt-1">
              <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className={BTN_SECONDARY}>
                {loadingMore && <Spinner />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExpertRow({ expert, onOpen }: { expert: ExpertCard; onOpen: () => void }) {
  const from = expertFromLabel(expert);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={classNames(
        CARD,
        'flex w-full items-start gap-3 p-3.5 text-left transition-colors hover:border-subtle',
      )}
    >
      <ExpertAvatar expert={expert} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex min-w-0 items-center gap-1">
            <span className="truncate text-[13px] font-medium text-primary">{expert.displayName}</span>
            {expert.verified && <VerifiedBadge />}
          </span>
          {from && <span className="shrink-0 text-[11px] text-secondary">{from}</span>}
        </div>
        <p className="line-clamp-2 text-[11.5px] leading-relaxed text-secondary">{expert.headline}</p>
        <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-[11px] text-muted">
          {expert.countries.length > 0 && <span>{expert.countries.map(countryName).join(', ')}</span>}
          <span>{completedLabel(expert.completedCount)}</span>
          {!expert.acceptingWork && <span className="text-warning">Not taking work</span>}
        </div>
      </div>
    </button>
  );
}

function ExpertAvatar({ expert, size = 36 }: { expert: Pick<ExpertCard, 'avatarUrl' | 'displayName'>; size?: number }) {
  const url = expert.avatarUrl?.startsWith('https://') ? expert.avatarUrl : null;
  return <ListingIcon url={url} name={expert.displayName} size={size} />;
}

/** An expert's profile over the panel: services, work, listings, and a request form. */
export function ExpertDrawer({
  handle,
  appId,
  onClose,
  onOpenListing,
}: {
  handle: string;
  appId: string;
  onClose: () => void;
  onOpenListing: (slug: string) => void;
}) {
  const profile = useResource(() => apiGetExpert(handle), handle);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const p = profile.data;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-surface" role="dialog" aria-label="Expert profile">
      <div className="flex items-center gap-2 border-b border-hairline px-4 py-3">
        <button type="button" onClick={onClose} className="ui-btn-icon" aria-label="Back to experts">
          <div className="i-ph:arrow-left text-[14px]" />
        </button>
        <h3 className="min-w-0 truncate text-[13px] font-medium text-primary">{p?.displayName ?? 'Expert'}</h3>
      </div>
      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {p ? (
          <ExpertBody profile={p} appId={appId} onOpenListing={onOpenListing} />
        ) : profile.error ? (
          <LoadError message="Could not load this profile." onRetry={() => void profile.reload()} />
        ) : (
          <>
            <SkeletonCard lines={3} />
            <SkeletonCard lines={4} />
          </>
        )}
      </div>
    </div>
  );
}

function ExpertBody({
  profile,
  appId,
  onOpenListing,
}: {
  profile: ExpertProfile;
  appId: string;
  onOpenListing: (slug: string) => void;
}) {
  const links = portfolioLinks(profile.portfolio);

  return (
    <>
      <header className="space-y-3">
        <div className="flex items-start gap-3">
          <ExpertAvatar expert={profile} size={44} />
          <div className="min-w-0 flex-1">
            <h2 className="inline-flex items-center gap-1.5 font-instrument text-[24px] leading-[1.05] tracking-[-0.02em] text-primary">
              {profile.displayName}
              {profile.verified && <VerifiedBadge />}
            </h2>
            <div className="mt-1 flex flex-wrap gap-x-2.5 text-[11px] text-muted">
              <span>@{profile.handle}</span>
              <span>{completedLabel(profile.completedCount)}</span>
            </div>
          </div>
        </div>
        <p className="text-[12.5px] leading-relaxed text-secondary">{profile.headline}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
          {profile.countries.length > 0 && <span>{profile.countries.map(countryName).join(', ')}</span>}
          {profile.languages.length > 0 && <span>{profile.languages.join(', ')}</span>}
        </div>
      </header>

      <Section title="About" icon="i-ph:text-align-left">
        <p className="whitespace-pre-line text-[12.5px] leading-relaxed text-secondary">{profile.about}</p>
      </Section>

      {profile.services.length > 0 && (
        <Section title="Services" icon="i-ph:briefcase">
          <ul className={classNames(CARD, 'divide-y divide-hairline')}>
            {profile.services.map((s) => (
              <li key={s.title} className="space-y-0.5 px-4 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12.5px] text-primary">{s.title}</span>
                  <span className="shrink-0 text-[11px] text-secondary">{serviceLine(s)}</span>
                </div>
                {s.description && <p className="text-[11.5px] leading-relaxed text-muted">{s.description}</p>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {links.length > 0 && (
        <Section title="Work" icon="i-ph:images">
          <ul className="space-y-1">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
                >
                  {l.label}
                  <div className="i-ph:arrow-up-right text-[11px]" />
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {profile.listings.length > 0 && (
        <Section title="In the marketplace" icon="i-ph:squares-four">
          <div className="space-y-2">
            {profile.listings.map((l) => (
              <ListingCard key={l.slug} item={l} onOpen={() => onOpenListing(l.slug)} />
            ))}
          </div>
        </Section>
      )}

      <RequestForm profile={profile} appId={appId} />
    </>
  );
}

function RequestForm({ profile, appId }: { profile: ExpertProfile; appId: string }) {
  const [draft, setDraft] = useState<ExpertRequestDraft>({ message: '', service: '', budget: '', attachApp: true });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const errors = expertRequestErrors(draft);
  const set = (patch: Partial<ExpertRequestDraft>) => setDraft((d) => ({ ...d, ...patch }));

  if (sent) {
    return (
      <section className={classNames(CARD, 'space-y-1 p-4')}>
        <div className="flex items-center gap-2 text-[12.5px] text-primary">
          <div className="i-ph:check-circle text-[14px] text-success" />
          Request sent to {profile.displayName}
        </div>
        <p className="text-[11.5px] text-muted">They'll reply to you by email.</p>
      </section>
    );
  }

  if (!profile.acceptingWork) {
    return (
      <section className={classNames(CARD, 'p-4 text-[12px] text-muted')}>
        {profile.displayName} isn't taking new work right now.
      </section>
    );
  }

  const submit = async () => {
    setTried(true);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await apiRequestExpert(profile.handle, expertRequestBody(draft, appId));
      toast.success('Request sent.');
      setSent(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send your request.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classNames(CARD, 'space-y-3 p-4')}
    >
      <h4 className={CAPTION}>Ask {profile.displayName} for help</h4>
      <Field label="What do you need?">
        <textarea
          value={draft.message}
          onChange={(e) => set({ message: e.target.value })}
          rows={4}
          maxLength={REQUEST_MESSAGE_MAX}
          disabled={busy}
          className="ui-input resize-y"
        />
      </Field>
      {tried && errors.message && <p className="text-[11px] text-danger">{errors.message}</p>}
      <div className="grid grid-cols-2 gap-2">
        {profile.services.length > 0 && (
          <Field label="Service">
            <Select value={draft.service} onChange={(v) => set({ service: v })} disabled={busy} label="Service">
              <option value="">Not sure yet</option>
              {profile.services.map((s) => (
                <option key={s.title} value={s.title}>
                  {s.title}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Budget in USD (optional)">
          <input
            value={draft.budget}
            onChange={(e) => set({ budget: e.target.value })}
            inputMode="decimal"
            placeholder="500"
            disabled={busy}
            className="ui-input !py-2"
          />
        </Field>
      </div>
      {tried && errors.budget && <p className="text-[11px] text-danger">{errors.budget}</p>}
      <label className="flex cursor-pointer items-center gap-2 text-[12px] text-secondary">
        <input
          type="checkbox"
          checked={draft.attachApp}
          onChange={(e) => set({ attachApp: e.target.checked })}
          disabled={busy}
          className="accent-accent"
        />
        It's about this app
      </label>
      {error && <ErrorBanner message={error} />}
      <button type="submit" disabled={busy} className="ui-btn-accent w-full justify-center !py-2 !text-[12.5px]">
        {busy && <Spinner />}
        Send request
      </button>
    </form>
  );
}
